import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Mailbox, Search, ExternalLink, MailOpen, Mail as MailIcon,
  CheckCheck, Trash2, CalendarCheck, RefreshCw, Loader2, Reply, Send, Forward, Paperclip, X,
} from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TEMPLATE_CAT_LABEL = { inkoop: "Inkoop", verkoop: "Verkoop", facturatie: "Facturatie" };

function TabButton({ tab, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`inbox-tab-${tab.email}`}
      className={`group relative flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-colors text-left ${
        active
          ? "bg-primary text-primary-foreground border-primary shadow-sm"
          : "bg-card text-foreground border-border hover:bg-secondary"
      }`}
    >
      <Mailbox className="h-4 w-4 shrink-0 opacity-80" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate max-w-[240px]">{tab.naam || tab.email}</div>
        <div className={`text-[11px] truncate max-w-[240px] ${active ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
          {tab.email}
        </div>
      </div>
      {tab.ongelezen > 0 && (
        <span
          data-testid={`inbox-tab-ongelezen-${tab.email}`}
          className={`ml-1 min-w-[22px] h-5 px-1.5 inline-flex items-center justify-center rounded-full text-[11px] font-semibold ${
            active ? "bg-primary-foreground text-primary" : "bg-accent text-accent-foreground"
          }`}
        >
          {tab.ongelezen}
        </span>
      )}
    </button>
  );
}

export default function Inbox() {
  const [tabs, setTabs] = useState([]);
  const [actief, setActief] = useState("");
  const [berichten, setBerichten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [alleenOngelezen, setAlleenOngelezen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState(null); // {onderwerp, body, van, naar, cc, bcc, mode, bijlagen}
  const [replyBusy, setReplyBusy] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [richting, setRichting] = useState("in"); // in | uit
  const [thread, setThread] = useState(null); // {items:[], root_id, aantal}
  const [templates, setTemplates] = useState([]);
  const [gekozenTemplate, setGekozenTemplate] = useState("");

  const loadTabs = useCallback(async (setActiveIfEmpty = false) => {
    try {
      const { data } = await api.get("/inbox/afzenders");
      setTabs(data || []);
      if (setActiveIfEmpty && !actief && data?.length) setActief(data[0].email);
    } catch { toast.error("Mailboxes laden mislukt"); }
  }, [actief]);

  const loadBerichten = useCallback(async () => {
    if (!actief) { setBerichten([]); setLoading(false); return; }
    setLoading(true);
    try {
      const params = { mailbox: actief, richting };
      if (richting === "in" && alleenOngelezen) params.ongelezen = 1;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/inbox", { params });
      setBerichten(data || []);
    } catch { toast.error("Berichten laden mislukt"); }
    finally { setLoading(false); }
  }, [actief, alleenOngelezen, q, richting]);

  useEffect(() => { loadTabs(true); }, [loadTabs]);
  useEffect(() => { loadBerichten(); }, [loadBerichten]);
  useEffect(() => {
    api.get("/email-templates")
      .then(({ data }) => setTemplates(data || []))
      .catch(() => setTemplates([]));
  }, []);

  const pasTemplateToe = (tid) => {
    setGekozenTemplate(tid);
    const t = templates.find(x => x.id === tid);
    if (!t || !reply) return;
    setReply(r => ({
      ...r,
      onderwerp: (t.onderwerp || "").trim() ? t.onderwerp : r.onderwerp,
      cc: t.cc || r.cc,
      bcc: t.bcc || r.bcc,
      body: (t.body || "") + (r.body || ""),
    }));
    if ((t.cc || "").trim() || (t.bcc || "").trim()) setShowCcBcc(true);
  };

  const openBericht = async (b) => {
    try {
      const { data } = await api.get(`/inbox/${b.id}`);
      setDetail(data);
      if (b.richting !== "uit" && !b.gelezen) {
        await api.post(`/inbox/${b.id}/gelezen`);
        setBerichten(bs => bs.map(x => x.id === b.id ? { ...x, gelezen: true } : x));
        loadTabs();
        window.dispatchEvent(new Event("inbox-updated"));
      }
      try {
        const { data: t } = await api.get(`/inbox/${b.id}/thread`);
        setThread(t);
      } catch { setThread(null); }
    } catch { toast.error("Bericht laden mislukt"); }
  };

  const markUnread = async (b) => {
    try {
      await api.post(`/inbox/${b.id}/ongelezen`);
      setBerichten(bs => bs.map(x => x.id === b.id ? { ...x, gelezen: false } : x));
      loadTabs();
      window.dispatchEvent(new Event("inbox-updated"));
    } catch { toast.error("Bijwerken mislukt"); }
  };

  const deleteBericht = async (b) => {
    if (!window.confirm("Bericht verwijderen?")) return;
    try {
      await api.delete(`/inbox/${b.id}`);
      setBerichten(bs => bs.filter(x => x.id !== b.id));
      if (detail?.id === b.id) setDetail(null);
      loadTabs();
      window.dispatchEvent(new Event("inbox-updated"));
    } catch { toast.error("Verwijderen mislukt"); }
  };

  const openReply = (b) => {
    if (!b) return;
    const onderwerp = (b.onderwerp || "").replace(/^\s*(re|fwd?):\s*/i, "");
    const bronTekst = b.tekst || (b.samenvatting || "").trim();
    const quoted = bronTekst
      ? "\n\n---\nOp " + fmtDate(b.ontvangen_op) + " schreef " + (b.van_naam || b.van) + ":\n" +
        bronTekst.split("\n").map(l => "> " + l).join("\n")
      : "";
    setReply({
      mode: "reply", bid: b.id, van: b.naar, naar: b.van,
      onderwerp: onderwerp ? `Re: ${onderwerp}` : "Re:",
      body: quoted, cc: "", bcc: "", bijlagen: [],
    });
    setShowCcBcc(false);
    setGekozenTemplate("");
  };

  const openForward = (b) => {
    if (!b) return;
    const onderwerp = (b.onderwerp || "").replace(/^\s*(re|fwd?):\s*/i, "");
    const bronTekst = b.tekst || (b.samenvatting || "").trim();
    const quoted = "\n\n---------- Doorgestuurd bericht ----------\n" +
      `Van: ${b.van_naam ? `${b.van_naam} <${b.van}>` : b.van}\n` +
      `Datum: ${fmtDate(b.ontvangen_op)}\n` +
      `Onderwerp: ${b.onderwerp || "(geen)"}\n` +
      `Aan: ${b.naar}\n\n` +
      (bronTekst || "(geen tekst)");
    setReply({
      mode: "forward", bid: b.id, van: b.naar, naar: "",
      onderwerp: onderwerp ? `Fwd: ${onderwerp}` : "Fwd:",
      body: quoted, cc: "", bcc: "", bijlagen: [],
    });
    setShowCcBcc(false);
    setGekozenTemplate("");
  };

  const verstuurReply = async () => {
    if (!reply) return;
    if (!reply.onderwerp.trim()) { toast.error("Onderwerp is verplicht"); return; }
    if (!reply.body.trim()) { toast.error("Bericht mag niet leeg zijn"); return; }
    if (reply.mode === "forward" && !reply.naar.trim()) { toast.error("Vul minimaal één ontvanger in"); return; }
    setReplyBusy(true);
    try {
      const fd = new FormData();
      fd.append("onderwerp", reply.onderwerp);
      fd.append("body", reply.body);
      fd.append("cc", reply.cc || "");
      fd.append("bcc", reply.bcc || "");
      if (reply.mode === "forward") fd.append("naar", reply.naar);
      (reply.bijlagen || []).forEach((f) => fd.append("bijlagen", f, f.name));
      const url = reply.mode === "forward"
        ? `/inbox/${reply.bid}/forward`
        : `/inbox/${reply.bid}/reply`;
      // Belangrijk: geen expliciete Content-Type meegeven — axios/browser voegt
      // de multipart-boundary automatisch toe. Handmatig 'multipart/form-data'
      // zetten zonder boundary → server kan de request niet parsen en de
      // frontend kan een leeg antwoord teruggekregen (wit scherm).
      await api.post(url, fd);
      toast.success(reply.mode === "forward"
        ? `Doorgestuurd naar ${reply.naar}`
        : `Antwoord verstuurd naar ${reply.naar}`);
      setReply(null);
      setDetail(null);
      setThread(null);
      await loadBerichten();
      await loadTabs();
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const msg = typeof raw === "string" ? raw
        : Array.isArray(raw) ? raw.map(x => x?.msg || JSON.stringify(x)).join("; ")
        : (raw ? JSON.stringify(raw) : "Versturen mislukt");
      toast.error(msg);
    } finally { setReplyBusy(false); }
  };

  const markeerAllesGelezen = async () => {    if (!actief) return;
    setBusy(true);
    try {
      await api.post(`/inbox/mailbox/${encodeURIComponent(actief)}/alles-gelezen`);
      await loadBerichten();
      await loadTabs();
      window.dispatchEvent(new Event("inbox-updated"));
      toast.success("Alles gemarkeerd als gelezen");
    } catch { toast.error("Bijwerken mislukt"); }
    finally { setBusy(false); }
  };

  const totalUnread = useMemo(() => tabs.reduce((s, t) => s + (t.ongelezen || 0), 0), [tabs]);

  return (
    <div className="space-y-6" data-testid="inbox-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm text-muted-foreground">Communicatie</div>
          <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Inbox</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Alle inkomende e-mails per Resend-adres. {totalUnread > 0 ? `${totalUnread} ongelezen.` : "Alles bijgewerkt."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { loadTabs(); loadBerichten(); }} data-testid="inbox-refresh">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Vernieuw
          </Button>
          {actief && (
            <Button variant="outline" size="sm" onClick={markeerAllesGelezen} disabled={busy} data-testid="inbox-alles-gelezen">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCheck className="h-4 w-4 mr-1.5" />}
              Alles gelezen
            </Button>
          )}
        </div>
      </div>

      {tabs.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-10 text-center">
          <Mailbox className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">
            Nog geen Resend-afzenders geregistreerd. Voeg een adres toe via{" "}
            <Link to="/instellingen/email" className="text-primary hover:underline">Instellingen → E-mail</Link>.
          </p>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="inbox-tabs">
          {tabs.map(t => (
            <TabButton key={t.email} tab={t} active={actief === t.email} onClick={() => setActief(t.email)} />
          ))}
        </div>
      )}

      {actief && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex rounded-lg border border-border bg-card p-1" data-testid="inbox-richting-switch">
            <button onClick={() => setRichting("in")}
              className={`px-3 py-1.5 text-sm rounded-md ${richting==="in"?"bg-primary text-primary-foreground":"text-muted-foreground hover:text-foreground"}`}
              data-testid="inbox-richting-in">Ontvangen</button>
            <button onClick={() => setRichting("uit")}
              className={`px-3 py-1.5 text-sm rounded-md ${richting==="uit"?"bg-primary text-primary-foreground":"text-muted-foreground hover:text-foreground"}`}
              data-testid="inbox-richting-uit">Verzonden</button>
          </div>
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Zoek op onderwerp, afzender of tekst…"
              value={q} onChange={(e) => setQ(e.target.value)} data-testid="inbox-zoek" />
          </div>
          {richting === "in" && (
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input type="checkbox" checked={alleenOngelezen} onChange={(e) => setAlleenOngelezen(e.target.checked)}
                className="h-4 w-4 accent-primary" data-testid="inbox-alleen-ongelezen" />
              Alleen ongelezen
            </label>
          )}
        </div>
      )}

      {actief && (
        <div className="bg-card border border-border rounded-xl divide-y divide-border overflow-hidden" data-testid="inbox-lijst">
          {loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Laden…
            </div>
          ) : berichten.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">Geen berichten in deze mailbox.</div>
          ) : (
            berichten.map(b => {
              const isUit = b.richting === "uit";
              return (
              <div key={b.id} className={`px-4 py-3 hover:bg-secondary/50 transition-colors flex items-start gap-3 ${!b.gelezen && !isUit ? "bg-secondary/30" : ""}`}
                data-testid={`inbox-rij-${b.id}`}>
                <button className="mt-1 shrink-0" onClick={() => (isUit ? openBericht(b) : (b.gelezen ? markUnread(b) : openBericht(b)))}
                  title={isUit ? "Verzonden bericht" : (b.gelezen ? "Markeer als ongelezen" : "Markeer als gelezen")}>
                  {isUit ? <Send className="h-4 w-4 text-muted-foreground" /> : (b.gelezen ? <MailOpen className="h-4 w-4 text-muted-foreground" /> : <MailIcon className="h-4 w-4 text-primary" />)}
                </button>
                <button className="flex-1 min-w-0 text-left" onClick={() => openBericht(b)}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm truncate ${!b.gelezen && !isUit ? "font-semibold" : "font-medium"}`}>
                      {isUit
                        ? <>Aan: <span className="text-muted-foreground">{b.naar || "—"}</span></>
                        : (b.van_naam || b.van || "Onbekende afzender")}
                    </span>
                    {isUit && b.soort === "forward" && <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-5">Doorgestuurd</Badge>}
                    {isUit && b.soort === "reply" && <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-5">Antwoord</Badge>}
                    {isUit && b.soort === "factuur" && <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-5 border-emerald-300 text-emerald-700">Factuur</Badge>}
                    {isUit && b.soort === "creditfactuur" && <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-5 border-rose-300 text-rose-700">Credit</Badge>}
                    {isUit && b.soort === "inkooporder" && <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-5 border-amber-300 text-amber-700">Inkoop</Badge>}
                    {isUit && b.soort === "inkooporder-doorzet" && <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-5 border-amber-300 text-amber-700">Doorzet</Badge>}
                    {isUit && b.soort === "pakbon" && <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-5 border-sky-300 text-sky-700">Pakbon</Badge>}
                    {b.inkooporder_nummer && (
                      <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-5">IO {b.inkooporder_nummer}</Badge>
                    )}
                    {b.herkende_leverdatum && (
                      <Badge className="text-[10px] py-0 px-1.5 h-5 bg-green-100 text-green-800 hover:bg-green-100">
                        <CalendarCheck className="h-3 w-3 mr-0.5" /> {fmtDate(b.herkende_leverdatum)}
                      </Badge>
                    )}
                  </div>
                  <div className={`text-sm mt-0.5 truncate ${!b.gelezen && !isUit ? "font-medium" : ""}`}>{b.onderwerp || "(geen onderwerp)"}</div>
                  {b.samenvatting && <div className="text-xs text-muted-foreground truncate mt-0.5">{b.samenvatting}</div>}
                </button>
                <div className="shrink-0 flex flex-col items-end gap-1">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(isUit ? b.verzonden_op : b.ontvangen_op)}</span>
                  <button onClick={() => deleteBericht(b)} className="text-muted-foreground hover:text-destructive"
                    data-testid={`inbox-verwijder-${b.id}`} title="Verwijderen">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              );
            })
          )}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setThread(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="pr-8">{detail?.onderwerp || "(geen onderwerp)"}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 overflow-y-auto flex-1 -mx-6 px-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm border-b border-border pb-3">
                <div><span className="text-muted-foreground">Van: </span><span className="font-medium">{detail.van_naam ? `${detail.van_naam} <${detail.van}>` : detail.van}</span></div>
                <div><span className="text-muted-foreground">Naar: </span><span className="font-medium">{detail.naar}</span></div>
                {(detail.cc && detail.cc.length > 0) && (
                  <div className="sm:col-span-2"><span className="text-muted-foreground">CC: </span><span className="font-medium">{Array.isArray(detail.cc) ? detail.cc.join(", ") : detail.cc}</span></div>
                )}
                <div>
                  <span className="text-muted-foreground">{detail.richting === "uit" ? "Verzonden: " : "Ontvangen: "}</span>
                  {fmtDate(detail.richting === "uit" ? detail.verzonden_op : detail.ontvangen_op)}
                </div>
                {detail.inkooporder_nummer && (
                  <div>
                    <span className="text-muted-foreground">Inkooporder: </span>
                    <Link to={`/inkooporders/${detail.inkooporder_id}`} className="text-primary hover:underline inline-flex items-center gap-1">
                      {detail.inkooporder_nummer} <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>
                )}
                {detail.herkende_leverdatum && (
                  <div className="sm:col-span-2">
                    <span className="text-muted-foreground">AI-herkende leverdatum: </span>
                    <span className="font-medium text-green-700">{fmtDate(detail.herkende_leverdatum)}</span>
                  </div>
                )}
                {detail.samenvatting && (
                  <div className="sm:col-span-2">
                    <span className="text-muted-foreground">Samenvatting: </span>
                    <span>{detail.samenvatting}</span>
                  </div>
                )}
              </div>
              {detail.html ? (
                <iframe
                  title="bericht"
                  className="w-full min-h-[420px] bg-white rounded-lg border border-border"
                  sandbox=""
                  srcDoc={detail.html}
                  data-testid="inbox-detail-html"
                />
              ) : (
                <pre className="whitespace-pre-wrap text-sm font-sans" data-testid="inbox-detail-tekst">{detail.tekst || "(leeg)"}</pre>
              )}
              {detail.bijlagen && detail.bijlagen.length > 0 && (
                <div className="border-t border-border pt-3">
                  <div className="text-xs uppercase text-muted-foreground tracking-wide mb-2">Bijlagen ({detail.bijlagen.length})</div>
                  <div className="flex flex-wrap gap-2">
                    {detail.bijlagen.map((att, i) => (
                      <span key={`${att.filename || "att"}-${i}`} className="inline-flex items-center gap-1.5 text-xs bg-secondary rounded-md px-2 py-1">
                        <Paperclip className="h-3 w-3" /> {att.filename}
                        {att.size > 0 && <span className="text-muted-foreground">({Math.round(att.size/1024)} KB)</span>}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {thread && thread.aantal > 1 && (
                <div className="border-t border-border pt-3" data-testid="inbox-thread">
                  <div className="text-xs uppercase text-muted-foreground tracking-wide mb-2">Conversatie ({thread.aantal})</div>
                  <div className="space-y-1.5">
                    {thread.items.map((it) => {
                      const isSelf = it.id === detail.id;
                      const isUit = it.richting === "uit";
                      const dt = isUit ? it.verzonden_op : it.ontvangen_op;
                      return (
                        <button key={it.id} type="button" disabled={isSelf}
                          onClick={() => openBericht(it)}
                          className={`w-full text-left flex items-start gap-2 rounded-md px-2 py-1.5 border ${isSelf ? "bg-primary/5 border-primary/40" : "border-border hover:bg-secondary"}`}
                          data-testid={`thread-item-${it.id}`}>
                          {isUit
                            ? <Send className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                            : <MailOpen className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />}
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium truncate">
                              {isUit
                                ? `${it.soort === "forward" ? "Doorgestuurd" : "Beantwoord"} — ${it.naar}`
                                : (it.van_naam || it.van || "Onbekend")}
                              {isSelf && <span className="ml-1.5 text-[10px] text-primary">(huidig)</span>}
                            </div>
                            <div className="text-[11px] text-muted-foreground truncate">{it.onderwerp || "(geen onderwerp)"}</div>
                          </div>
                          <span className="text-[10px] text-muted-foreground whitespace-nowrap">{fmtDate(dt)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="border-t border-border pt-4">
            {detail && (
              <>
                {detail.richting !== "uit" && (
                  <>
                    <Button onClick={() => openReply(detail)} data-testid="inbox-detail-reply">
                      <Reply className="h-4 w-4 mr-1.5" /> Beantwoorden
                    </Button>
                    <Button variant="outline" onClick={() => openForward(detail)} data-testid="inbox-detail-forward">
                      <Forward className="h-4 w-4 mr-1.5" /> Doorsturen
                    </Button>
                    <Button variant="ghost" onClick={() => { markUnread(detail); setDetail(null); }} data-testid="inbox-detail-ongelezen">
                      <MailIcon className="h-4 w-4 mr-1.5" /> Markeer ongelezen
                    </Button>
                  </>
                )}
                <Button variant="outline" onClick={() => deleteBericht(detail)} className="text-destructive hover:text-destructive"
                  data-testid="inbox-detail-verwijder">
                  <Trash2 className="h-4 w-4 mr-1.5" /> Verwijderen
                </Button>
                <Button variant="ghost" onClick={() => setDetail(null)} data-testid="inbox-detail-sluit">Sluit</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reply} onOpenChange={(o) => !o && setReply(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{reply?.mode === "forward" ? "Doorsturen" : "Beantwoorden"}</DialogTitle>
          </DialogHeader>
          {reply && (
            <div className="space-y-3">
              {templates.length > 0 && (
                <div data-testid="reply-template-blok">
                  <Label className="text-xs text-muted-foreground">Eigen sjabloon invoegen</Label>
                  <Select value={gekozenTemplate} onValueChange={pasTemplateToe}>
                    <SelectTrigger data-testid="reply-template-select" className="mt-1">
                      <SelectValue placeholder="Kies een sjabloon…" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map(t => (
                        <SelectItem key={t.id} value={t.id} data-testid={`reply-template-optie-${t.id}`}>
                          <span className="text-muted-foreground">{TEMPLATE_CAT_LABEL[t.categorie] || t.categorie}</span>
                          {" · "}{t.naam}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    De sjabloontekst wordt bovenaan het bericht gezet; onderwerp, CC en BCC worden overgenomen indien ingevuld.
                  </p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-xs text-muted-foreground">Van</Label>
                  <div className="text-sm font-medium truncate">{reply.van}</div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Naar {reply.mode === "forward" && <span className="text-muted-foreground">(scheid met komma)</span>}</Label>
                  {reply.mode === "forward" ? (
                    <Input value={reply.naar}
                      onChange={(e) => setReply({ ...reply, naar: e.target.value })}
                      placeholder="collega@nextproduct.nl, leverancier@bedrijf.nl"
                      data-testid="forward-naar" />
                  ) : (
                    <div className="text-sm font-medium truncate">{reply.naar}</div>
                  )}
                </div>
              </div>
              {!showCcBcc && (
                <button type="button" onClick={() => setShowCcBcc(true)}
                  className="text-xs text-primary hover:underline" data-testid="reply-toggle-ccbcc">
                  + CC / BCC toevoegen
                </button>
              )}
              {showCcBcc && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">CC</Label>
                    <Input value={reply.cc || ""}
                      onChange={(e) => setReply({ ...reply, cc: e.target.value })}
                      placeholder="cc@example.com, cc2@example.com"
                      data-testid="reply-cc" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">BCC</Label>
                    <Input value={reply.bcc || ""}
                      onChange={(e) => setReply({ ...reply, bcc: e.target.value })}
                      placeholder="bcc@example.com"
                      data-testid="reply-bcc" />
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs text-muted-foreground">Onderwerp</Label>
                <Input value={reply.onderwerp}
                  onChange={(e) => setReply({ ...reply, onderwerp: e.target.value })}
                  data-testid="reply-onderwerp" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Bericht</Label>
                <Textarea rows={12} value={reply.body}
                  onChange={(e) => setReply({ ...reply, body: e.target.value })}
                  placeholder="Typ je bericht hier…"
                  data-testid="reply-body" />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Je handtekening en logo worden automatisch onderaan toegevoegd.
                </p>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">Bijlagen {reply.bijlagen?.length > 0 && `(${reply.bijlagen.length})`}</Label>
                  <label className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline cursor-pointer" data-testid="reply-add-bijlage">
                    <Paperclip className="h-3.5 w-3.5" /> Bestand toevoegen
                    <input type="file" multiple className="hidden"
                      onChange={(e) => {
                        const nieuw = Array.from(e.target.files || []);
                        const teGroot = nieuw.filter(f => f.size > 20 * 1024 * 1024);
                        if (teGroot.length > 0) {
                          toast.error(`Bestand(en) te groot (max 20 MB): ${teGroot.map(f => f.name).join(", ")}`);
                        }
                        const ok = nieuw.filter(f => f.size <= 20 * 1024 * 1024);
                        setReply(r => ({ ...r, bijlagen: [...(r.bijlagen || []), ...ok] }));
                        e.target.value = "";
                      }} />
                  </label>
                </div>
                {reply.bijlagen?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {reply.bijlagen.map((f, i) => (
                      <span key={`${f.name}-${f.size}-${i}`} className="inline-flex items-center gap-1 text-xs bg-secondary rounded-md pl-2 pr-1 py-1"
                        data-testid={`reply-bijlage-${i}`}>
                        <Paperclip className="h-3 w-3" /> {f.name}
                        <span className="text-muted-foreground">({Math.round(f.size/1024)} KB)</span>
                        <button type="button" onClick={() =>
                          setReply(r => ({ ...r, bijlagen: r.bijlagen.filter((_, j) => j !== i) }))
                        } className="ml-0.5 hover:text-destructive" title="Verwijderen">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReply(null)} disabled={replyBusy}>Annuleren</Button>
            <Button onClick={verstuurReply} disabled={replyBusy} data-testid="reply-verstuur">
              {replyBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
              {replyBusy ? "Versturen…" : "Verstuur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
